import { useEffect, useRef, useState } from "react";
import {
  MicrophoneStage,
  SpeakerHigh,
  SpeakerSlash,
  X,
  ChatText,
  ArrowClockwise,
} from "@phosphor-icons/react";
import api from "../../lib/api";
import { toast } from "sonner";

/**
 * VoiceQuickResponse — surfaces the AI CFO's reply to a voice command without
 * opening the full chat drawer. Triggered by WakeWord when the user says
 * something like "Hey CFO, cuánto me debe Sergio".
 *
 * Flow:
 *   1. POST /finance/chat with `question`
 *   2. Once reply arrives, POST /finance/voice/speak to get an mp3 blob
 *   3. Auto-play the audio; user can mute or open the full chat with this
 *      exchange preserved (we return the session_id via `onOpenChat`).
 *   4. Auto-dismisses after 25s or on manual close.
 */
export default function VoiceQuickResponse({ question, scope, onClose, onOpenChat }) {
  const [state, setState] = useState({ loading: true, reply: "", error: null, sessionId: null });
  const [muted, setMuted] = useState(() => window.localStorage.getItem("cfo_voice_muted") === "1");
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const cancelRef = useRef(false);

  useEffect(() => {
    window.localStorage.setItem("cfo_voice_muted", muted ? "1" : "0");
  }, [muted]);

  useEffect(() => {
    cancelRef.current = false;
    (async () => {
      try {
        const { data } = await api.post("/finance/chat", {
          message: question,
          personality: "friendly",
          scope: scope || "all",
          language: "es",
        });
        if (cancelRef.current) return;
        setState({ loading: false, reply: data.reply, error: null, sessionId: data.session_id });

        // Auto-play TTS (unless muted)
        if (muted) return;
        try {
          const blobRes = await api.post(
            "/finance/voice/speak",
            {
              text: (data.reply || "").slice(0, 900), // ElevenLabs latency budget
              voice: "female",
              response_format: "audio",
            },
            { responseType: "blob" },
          );
          if (cancelRef.current) return;
          const url = URL.createObjectURL(blobRes.data);
          const audio = new Audio(url);
          audioRef.current = audio;
          audio.addEventListener("play", () => setPlaying(true));
          audio.addEventListener("pause", () => setPlaying(false));
          audio.addEventListener("ended", () => setPlaying(false));
          try {
            await audio.play();
          } catch (playErr) {
            // Autoplay blocked — user can click the speaker icon
            toast.info("Autoplay bloqueado — toca el altavoz para escuchar");
          }
        } catch { /* TTS is optional */ }
      } catch (err) {
        if (cancelRef.current) return;
        setState({
          loading: false,
          reply: "",
          error: err.response?.data?.detail || "No pude procesar tu pregunta",
          sessionId: null,
        });
      }
    })();
    return () => {
      cancelRef.current = true;
      if (audioRef.current) {
        try { audioRef.current.pause(); } catch { /* noop */ }
        audioRef.current = null;
      }
    };
  }, [question, scope]);

  // Auto-dismiss after 25s (paused while audio is playing)
  useEffect(() => {
    if (playing || state.loading) return;
    const t = setTimeout(onClose, 25000);
    return () => clearTimeout(t);
  }, [playing, state.loading, onClose]);

  const toggleMute = () => {
    setMuted((m) => !m);
    if (audioRef.current) {
      if (!muted) {
        try { audioRef.current.pause(); } catch { /* noop */ }
      } else {
        audioRef.current.play().catch(() => {});
      }
    }
  };

  const openInChat = () => {
    onOpenChat?.(state.sessionId);
  };

  return (
    <div
      data-testid="voice-quick-response"
      className="force-dark-scope fixed bottom-6 right-6 z-40 w-[380px] max-w-[95vw] rounded-2xl border border-white/12 bg-[#0F1017] shadow-2xl overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-start gap-2 p-3 border-b border-white/8 bg-[#141621]">
        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
            playing ? "bg-emerald-500/25 animate-pulse" : "bg-[#FACC15]/20"
          }`}
        >
          <MicrophoneStage
            size={16}
            weight="fill"
            className={playing ? "text-emerald-300" : "text-[#FACC15]"}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[9px] font-mono uppercase tracking-widest text-[#FACC15]">
            Voice CFO · Respuesta rápida
          </div>
          <div className="text-[13px] font-heading font-bold text-white leading-tight mt-0.5 truncate">
            &ldquo;{question}&rdquo;
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            data-testid="voice-quick-mute"
            onClick={toggleMute}
            title={muted ? "Reactivar voz" : "Silenciar"}
            className="p-1.5 rounded-md text-white/50 hover:text-white hover:bg-white/8"
          >
            {muted ? <SpeakerSlash size={13} weight="bold" /> : <SpeakerHigh size={13} weight="bold" />}
          </button>
          <button
            data-testid="voice-quick-close"
            onClick={onClose}
            title="Cerrar"
            className="p-1.5 rounded-md text-white/50 hover:text-white hover:bg-white/8"
          >
            <X size={13} weight="bold" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="p-3 max-h-[280px] overflow-y-auto">
        {state.loading ? (
          <div className="flex items-center gap-2 text-[11px] font-mono text-white/50 py-3">
            <ArrowClockwise size={13} weight="bold" className="animate-spin" />
            Consultando al CFO...
          </div>
        ) : state.error ? (
          <div className="text-[12px] font-mono text-red-300">
            {state.error}
          </div>
        ) : (
          <div
            data-testid="voice-quick-reply"
            className="text-[13px] text-white/90 leading-relaxed whitespace-pre-wrap"
          >
            {state.reply}
          </div>
        )}
      </div>

      {/* Footer */}
      {!state.loading && state.sessionId && (
        <div className="flex items-center justify-between px-3 py-2 border-t border-white/8 bg-[#0F1017]">
          <span className="text-[9px] font-mono uppercase tracking-widest text-white/40">
            {playing ? "Reproduciendo..." : "Auto-cierra en 25 s"}
          </span>
          <button
            data-testid="voice-quick-open-chat"
            onClick={openInChat}
            className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#FACC15] hover:text-[#EAB308]"
          >
            <ChatText size={11} weight="bold" />
            Ver en chat
          </button>
        </div>
      )}
    </div>
  );
}
